import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Sparkles, ArrowUpRight, Repeat, CalendarClock, Users, Loader2, Mail, CornerUpLeft, Send, MessageSquare, Flame, X } from "lucide-react";
import { fmtNumber, relativeTime } from "@/lib/utils";

const STATUS_COLOR = {
  active: "text-signal-500",
  draft: "text-muted-foreground",
  paused: "text-amber-accent",
};

function fmtNextRun(v, tz) {
  if (!v) return "—";
  if (v === "now") return "sending this week";
  try {
    const d = new Date(v);
    const opts = { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" };
    if (tz) opts.timeZone = tz;
    return d.toLocaleString(undefined, opts);
  } catch { return v; }
}

function RecurringSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testBusy, setTestBusy] = useState("");
  const [smsDrafts, setSmsDrafts] = useState({});
  const [smsBusy, setSmsBusy] = useState("");
  const [smsTestTo, setSmsTestTo] = useState("");
  const [replies, setReplies] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/recurring-campaigns");
      setItems(data.campaigns || []);
      const drafts = {};
      (data.campaigns || []).forEach((c) => {
        drafts[c.id] = {
          sms_enabled: !!c.sms_enabled, sms_sender: c.sms_sender || "",
          sms_body: c.sms_body || "", sms_delay_days: c.sms_delay_days ?? 2,
        };
      });
      setSmsDrafts(drafts);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/recurring-campaigns/replies");
      setReplies(data.replies || []);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const dismissReply = async (pid) => {
    setReplies((r) => r.filter((x) => x.id !== pid));
    try { await api.post(`/recurring-campaigns/replies/${pid}/dismiss`); }
    catch { toast.error("Could not clear that reply"); await load(); }
  };

  const setDraft = (id, patch) => setSmsDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const saveSms = async (c) => {
    setSmsBusy(c.id + ":save");
    try {
      await api.post(`/recurring-campaigns/${c.id}/sms`, smsDrafts[c.id]);
      toast.success(smsDrafts[c.id].sms_enabled ? "SMS nudge saved & on" : "SMS nudge saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save SMS settings"); }
    setSmsBusy("");
  };

  const sendTestSms = async (c) => {
    const to = (smsTestTo || "").trim();
    if (!to) { toast.error("Enter a mobile number (with country code, e.g. +386…)"); return; }
    setSmsBusy(c.id + ":test");
    try {
      const { data } = await api.post(`/recurring-campaigns/${c.id}/send-test-sms`, { to });
      toast.success(`Test SMS sent to ${data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test SMS"); }
    setSmsBusy("");
  };

  const seed = async () => {
    setBusy("seed");
    try {
      await api.post("/recurring-campaigns/seed-cargo-weekly");
      toast.success("Weekly warehouse-availability campaign created", { description: "Sends every Wednesday 12:00 (Ljubljana) to your Partnership prospects." });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the weekly campaign");
    }
    setBusy("");
  };

  const seedEuropacific = async () => {
    setBusy("seed-eu");
    try {
      await api.post("/recurring-campaigns/seed-europacific-weekly");
      toast.success("Europacific weekly outreach created", { description: "8 rotating emails, every Tuesday 12:00 (Ljubljana), to all prospects with an email." });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the Europacific weekly campaign");
    }
    setBusy("");
  };

  const sendTest = async (c) => {
    const to = (testEmail || "").trim();
    if (!to) { toast.error("Enter an email address to send the test to"); return; }
    setTestBusy(c.id);
    try {
      const { data } = await api.post(`/recurring-campaigns/${c.id}/send-test`, { to });
      toast.success(`Test email sent to ${data.to}`, { description: `Variation shown: ${data.variation}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the test email");
    }
    setTestBusy("");
  };

  const toggle = async (c) => {
    setBusy(c.id);
    const action = c.status === "active" ? "pause" : "resume";
    try {
      await api.post(`/recurring-campaigns/${c.id}/${action}`);
      toast.success(action === "pause" ? "Weekly email turned off" : "Weekly email turned on");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
    setBusy("");
  };

  if (loading) return null;

  return (
    <div className="mb-8" data-testid="recurring-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Repeat className="w-4 h-4 text-signal-500" />
          <h2 className="font-display font-semibold tracking-tight text-lg">Recurring weekly</h2>
        </div>
        {items.length === 0 && (
          <div className="flex items-center gap-2">
            <Button onClick={seed} disabled={!!busy} size="sm" className="rounded-sm" data-testid="recurring-seed-btn">
              {busy === "seed" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
              Cargo CFS warehouse email
            </Button>
            <Button onClick={seedEuropacific} disabled={!!busy} size="sm" variant="outline" className="rounded-sm" data-testid="recurring-seed-eu-btn">
              {busy === "seed-eu" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
              Europacific weekly (8 emails)
            </Button>
          </div>
        )}
      </div>

      {replies.length > 0 && (
        <div className="mb-3 rounded-sm border border-amber-accent/40 bg-amber-accent/5 p-4" data-testid="recurring-replies">
          <div className="flex items-center gap-2 mb-2.5">
            <Flame className="w-4 h-4 text-amber-accent" />
            <h3 className="font-display font-semibold tracking-tight text-sm">Replies to your weekly emails — please take over</h3>
            <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded-sm bg-amber-accent/20 text-amber-accent" data-testid="recurring-replies-count">{replies.length}</span>
          </div>
          <div className="space-y-2">
            {replies.map((r) => (
              <div key={r.id} className="flex items-start justify-between gap-3 rounded-sm bg-background border border-border px-3 py-2.5" data-testid={`recurring-reply-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {r.full_name || r.email}{r.company ? <span className="text-muted-foreground font-normal"> · {r.company}</span> : null}
                  </div>
                  {r.recurring_reply_excerpt ? (
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">“{r.recurring_reply_excerpt}”</div>
                  ) : null}
                  <div className="text-[0.65rem] text-muted-foreground mt-1 inline-flex items-center gap-1.5 flex-wrap">
                    <span>{r.recurring_reply_campaign_name || "Weekly email"}</span>
                    {r.recurring_reply_at ? <span>· {relativeTime(r.recurring_reply_at)}</span> : null}
                    {r.recurring_reply_alert_ok ? <span className="text-signal-500">· team notified</span> : <span className="text-amber-accent">· alert not sent</span>}
                  </div>
                </div>
                <button onClick={() => dismissReply(r.id)} className="shrink-0 p-1 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Clear" data-testid={`recurring-reply-dismiss-${r.id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm px-5 py-6 text-sm text-muted-foreground" data-testid="recurring-empty">
          Send one auto-rotating email to your Partnership prospects every week (4 variations + 5 CTAs randomized so it reads like an ops update, never a blast). Set it up once and it runs until you pause it.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((c, i) => (
            <div key={c.id} data-testid={`recurring-card-${i}`}
              className="border border-border rounded-sm p-4 bg-muted/20">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold tracking-tight truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1.5">
                    <CalendarClock className="w-3 h-3" /> Every {c.weekday_label} {String(c.hour).padStart(2, "0")}:{String(c.minute || 0).padStart(2, "0")} · {c.tz}
                  </div>
                </div>
                <span className={`text-[0.6rem] tracking-widest uppercase whitespace-nowrap ${STATUS_COLOR[c.status] || "text-muted-foreground"}`}>● {c.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="rounded-sm bg-background border border-border py-2">
                  <div className="text-lg font-mono font-semibold inline-flex items-center gap-1 justify-center"><Users className="w-3.5 h-3.5 text-muted-foreground" />{fmtNumber(c.audience_count)}</div>
                  <div className="text-[0.6rem] uppercase tracking-wider text-muted-foreground">Audience</div>
                </div>
                <div className="rounded-sm bg-background border border-border py-2">
                  <div className="text-lg font-mono font-semibold">{fmtNumber(c.metrics?.sent)}</div>
                  <div className="text-[0.6rem] uppercase tracking-wider text-muted-foreground">Sent</div>
                </div>
                <div className="rounded-sm bg-background border border-border py-2">
                  <div className="text-lg font-mono font-semibold">{fmtNumber(c.metrics?.weeks_run)}</div>
                  <div className="text-[0.6rem] uppercase tracking-wider text-muted-foreground">Weeks</div>
                </div>
              </div>
              {c.reply_to?.length ? (
                <div className="mt-3 text-xs text-muted-foreground inline-flex items-start gap-1.5" data-testid={`recurring-replyto-${i}`}>
                  <CornerUpLeft className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Replies go to <span className="text-foreground">{c.reply_to.join(", ")}</span></span>
                </div>
              ) : null}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <div className="text-xs text-muted-foreground">Next: <span className="text-foreground font-medium">{fmtNextRun(c.next_run, c.tz)}</span></div>
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={`recurring-toggle-${i}`}>
                  <span className={`text-xs font-medium ${c.status === "active" ? "text-signal-500" : "text-muted-foreground"}`}>{c.status === "active" ? "On" : "Off"}</span>
                  {busy === c.id
                    ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    : <Switch checked={c.status === "active"} onCheckedChange={() => toggle(c)} data-testid={`recurring-switch-${i}`} />}
                </label>
              </div>
              {c.upcoming_runs?.length ? (
                <div className="mt-3">
                  <button onClick={() => setScheduleOpen((s) => ({ ...s, [c.id]: !s[c.id] }))}
                    className="text-xs font-medium text-signal-500 inline-flex items-center gap-1.5 hover:opacity-80"
                    data-testid={`recurring-schedule-toggle-${i}`}>
                    <CalendarClock className="w-3.5 h-3.5" />
                    {scheduleOpen[c.id] ? "Hide schedule" : "View schedule — next sends"}
                  </button>
                  {scheduleOpen[c.id] && (
                    <div className="mt-2 rounded-sm border border-border bg-background p-3" data-testid={`recurring-schedule-${i}`}>
                      <div className="text-[0.6rem] uppercase tracking-wider text-muted-foreground mb-2">Upcoming weekly sends</div>
                      <ol className="space-y-1.5">
                        {c.upcoming_runs.map((iso, idx) => (
                          <li key={iso} className="flex items-center gap-2 text-xs" data-testid={`recurring-schedule-date-${i}-${idx}`}>
                            <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[0.6rem] font-mono ${idx === 0 ? "bg-signal-500 text-background" : "bg-muted text-muted-foreground"}`}>{idx + 1}</span>
                            <span className={idx === 0 ? "text-foreground font-medium" : "text-muted-foreground"}>{fmtNextRun(iso, c.tz)}</span>
                            {idx === 0 && <span className="text-[0.55rem] uppercase tracking-wider text-signal-500 ml-auto">next</span>}
                          </li>
                        ))}
                      </ol>
                      <div className="text-[0.65rem] text-muted-foreground mt-2.5 pt-2.5 border-t border-border">
                        Each send goes to all {fmtNumber(c.audience_count)} in the audience. Every recipient gets one randomly-rotated variation{c.variations?.length ? ` of ${c.variations.length}` : ""} that week.
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Send a test to you@company.com" className="h-9 text-xs rounded-sm"
                  data-testid={`recurring-test-email-${i}`} />
                <Button onClick={() => sendTest(c)} disabled={testBusy === c.id} variant="outline" size="sm" className="rounded-sm shrink-0" data-testid={`recurring-sendtest-${i}`}>
                  {testBusy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" />Send test</>}
                </Button>
              </div>

              {smsDrafts[c.id] && (
                <div className="mt-3 pt-3 border-t border-border space-y-2.5" data-testid={`recurring-sms-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-3.5 h-3.5 text-signal-500" />
                      <span className="text-xs font-semibold">SMS follow-up</span>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <span className={`text-xs font-medium ${smsDrafts[c.id].sms_enabled ? "text-signal-500" : "text-muted-foreground"}`}>{smsDrafts[c.id].sms_enabled ? "On" : "Off"}</span>
                      <Switch checked={smsDrafts[c.id].sms_enabled} onCheckedChange={(v) => setDraft(c.id, { sms_enabled: v })} data-testid={`recurring-sms-toggle-${i}`} />
                    </label>
                  </div>
                  <p className="text-[0.7rem] text-muted-foreground">
                    Texts prospects who have a mobile but haven't replied, a set number of days after the weekly email.
                  </p>
                  <div className="flex items-center gap-2">
                    <Input value={smsDrafts[c.id].sms_sender} onChange={(e) => setDraft(c.id, { sms_sender: e.target.value })}
                      placeholder="Sender: +386… number or 'Cargo CFS'" className="h-9 text-xs rounded-sm" data-testid={`recurring-sms-sender-${i}`} />
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Input type="number" min="0" value={smsDrafts[c.id].sms_delay_days}
                        onChange={(e) => setDraft(c.id, { sms_delay_days: Number(e.target.value) })}
                        className="h-9 w-16 text-xs rounded-sm text-center" data-testid={`recurring-sms-delay-${i}`} />
                      <span className="text-[0.7rem] text-muted-foreground">days after</span>
                    </div>
                  </div>
                  <textarea value={smsDrafts[c.id].sms_body} onChange={(e) => setDraft(c.id, { sms_body: e.target.value })}
                    rows={2} maxLength={320} placeholder="SMS text (keep it short — supports {{first_name}})"
                    className="w-full text-xs rounded-sm border border-border bg-background px-3 py-2 resize-none" data-testid={`recurring-sms-body-${i}`} />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[0.65rem] text-muted-foreground">{(smsDrafts[c.id].sms_body || "").length} chars · {(smsDrafts[c.id].metrics?.sms_sent ?? c.metrics?.sms_sent) || 0} sent</span>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => saveSms(c)} disabled={smsBusy === c.id + ":save"} size="sm" variant="outline" className="rounded-sm" data-testid={`recurring-sms-save-${i}`}>
                        {smsBusy === c.id + ":save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input value={smsTestTo} onChange={(e) => setSmsTestTo(e.target.value)}
                      placeholder="Test to +386…" className="h-9 text-xs rounded-sm" data-testid={`recurring-sms-test-to-${i}`} />
                    <Button onClick={() => sendTestSms(c)} disabled={smsBusy === c.id + ":test"} size="sm" variant="outline" className="rounded-sm shrink-0" data-testid={`recurring-sms-test-btn-${i}`}>
                      {smsBusy === c.id + ":test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><MessageSquare className="w-3.5 h-3.5 mr-1.5" />Test SMS</>}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CampaignsPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/campaigns").then((r) => setItems(r.data.campaigns || [])); }, []);

  return (
    <DashboardLayout
      eyebrow="Campaigns"
      title="Outbound campaigns"
      actions={
        <Link to="/app/campaigns/new" data-testid="campaigns-new-btn">
          <Button className="rounded-sm">New campaign <Sparkles className="w-4 h-4 ml-1.5" /></Button>
        </Link>
      }
    >
      <RecurringSection />

      <div className="border border-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 bg-muted/40 text-[0.7rem] uppercase tracking-wider text-muted-foreground font-semibold">
          <div className="col-span-5">Campaign</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Sent</div>
          <div className="col-span-1 text-right">Replied</div>
          <div className="col-span-1 text-right">Meetings</div>
          <div className="col-span-2 text-right">Created</div>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">
            No campaigns yet — create your first.
          </div>
        )}
        {items.map((c, i) => (
          <Link
            to={`/app/campaigns/${c.id}`}
            key={c.id}
            data-testid={`campaign-row-${i}`}
            className="grid grid-cols-12 px-5 py-4 border-t border-border hover:bg-muted/30 transition-colors items-center"
          >
            <div className="col-span-5 min-w-0">
              <div className="font-display font-semibold tracking-tight truncate">{c.name}</div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">{c.target_segment}</div>
            </div>
            <div className="col-span-2">
              <span className={`text-[0.65rem] tracking-widest uppercase ${STATUS_COLOR[c.status] || "text-muted-foreground"}`}>● {c.status}</span>
            </div>
            <div className="col-span-1 text-right font-mono text-sm">{fmtNumber(c.metrics?.sent)}</div>
            <div className="col-span-1 text-right font-mono text-sm">{fmtNumber(c.metrics?.replied)}</div>
            <div className="col-span-1 text-right font-mono text-sm">{fmtNumber(c.metrics?.meetings)}</div>
            <div className="col-span-2 text-right text-xs text-muted-foreground inline-flex items-center justify-end gap-1">
              {relativeTime(c.created_at)} <ArrowUpRight className="w-3 h-3" />
            </div>
          </Link>
        ))}
      </div>
    </DashboardLayout>
  );
}
